import { Icons, Tooltip } from '@commenda/kakashi';

type TBuilderContextPillProps = {
  /** What the agent will be told about. Null renders nothing rather than an empty pill. */
  label: string | null;
  onRemove: () => void;
};

/** What the agent is about to be told about the author's screen, and a way to take it back.
 *
 * Shown rather than attached silently because this context is a claim about intent: saying "this
 * step is selected" tells the agent what "this" means, and being wrong about that is worse than
 * saying nothing. Removing it affects the next message only and leaves the conversation and the
 * canvas alone.
 *
 * A deliberate copy of `ChatPanelPill` in the client chat, not a second design: this package cannot
 * import from apps/enterprise, so the two are to be merged into one shared pill later. Keep them
 * looking identical until then. */
export const BuilderContextPill = ({
  label,
  onRemove,
}: TBuilderContextPillProps) => {
  if (!label) return null;

  return (
    <div className="flex w-fit min-w-0 max-w-full items-center gap-sm rounded-sm border-1/2 border-border-secondary bg-bg-primary py-xs pl-md pr-[10px] text-text-primary">
      {/* The label truncates to keep the pill small, so hovering it gives the name in full. */}
      <Tooltip content={label} asChild>
        <span className="min-w-0 truncate font-mulish text-xs font-medium leading-[15px] tracking-[-2%]">
          {label}
        </span>
      </Tooltip>
      <button
        type="button"
        aria-label="Stop sharing this screen with the agent"
        onClick={onRemove}
        className="flex size-3 shrink-0 cursor-pointer items-center justify-center border-none bg-transparent p-none text-text-secondary transition-colors hover:text-text-primary"
      >
        <Icons.XClose className="size-3" aria-hidden="true" />
      </button>
    </div>
  );
};
